import { useCallback, useState } from "react";
import { ROUTES } from "@oshimap/config";
import { relativeDay } from "@oshimap/utils";
import { ep } from "../../lib/api";
import { useQuery } from "../../lib/use-query";
import { navigate } from "../../app/router";
import { Button, EmptyState, ErrorState, Skeleton } from "../../components/ui";
import "./notification.css";
import { queryKeys } from "@oshimap/query";

/**
 * 실측: 알림 payload 는 타입마다 다르다. 목적지를 추측하지 않고,
 * placeId / eventId / reportId 가 실제로 들어 있을 때만 링크로 만든다.
 */
function destination(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  const s = (k: string) => (typeof payload[k] === "string" ? (payload[k] as string) : null);
  const place = s("placeId"), event = s("eventId"), report = s("reportId");
  if (place) return ROUTES.place(place);
  if (event) return ROUTES.event(event);
  if (report) return ROUTES.meReport(report);
  return null;
}

export function NotificationPane() {
  const list = useQuery(queryKeys.notifications(), (signal) => ep.listNotifications({ signal }));
  const [busy, setBusy] = useState(false);

  const open = useCallback(async (id: string, readAt: string | null, to: string | null) => {
    if (!readAt) { try { await ep.readNotification(id); list.reload(); } catch { /* 읽음 표시 실패가 이동을 막지 않는다 */ } }
    if (to) navigate(to);
  }, [list]);

  if (list.state.status === "loading" || list.state.status === "idle") {
    return <div className="om-me__pad"><Skeleton lines={3} /></div>;
  }
  if (list.state.status === "error") {
    return <div className="om-me__pad">
      <ErrorState title="알림을 불러오지 못했어요" body={list.state.error.message} onRetry={list.reload} />
    </div>;
  }

  const items = list.state.data;
  const unread = items.filter((n) => !n.readAt).length;

  if (items.length === 0) {
    return <div className="om-me__pad">
      {/* 비었을 때는 이게 이 구역의 제목이다 — h1 다음은 h2 여야 한다. */}
      <EmptyState level={2} title="아직 알림이 없어요"
        body="저장한 곳이 바뀌거나 제보가 검수되면 여기로 알려드릴게요." />
    </div>;
  }

  return (
    <div className="om-me__pad">
      <div className="om-noti__head">
        <h2 className="om-section__title" style={{ padding: 0 }}>
          알림 {unread > 0 ? <span className="num om-noti__unread">{unread}</span> : null}
        </h2>
        {unread > 0 ? (
          <Button variant="ghost" size="sm" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try { await ep.readAllNotifications(); list.reload(); } finally { setBusy(false); }
            }}>모두 읽음</Button>
        ) : null}
      </div>

      <ul className="om-noti">
        {items.map((n) => {
          const to = destination(n.payload);
          return (
            <li key={n.id} className={n.readAt ? "" : "om-noti--new"}>
              <button type="button" className="om-noti__row" onClick={() => void open(n.id, n.readAt, to)}>
                <span className="om-noti__dot" aria-hidden="true" />
                <span className="om-noti__text">
                  <b>{n.title}</b>
                  {n.body ? <small>{n.body}</small> : null}
                  <time className="num" dateTime={n.createdAt}>{relativeDay(n.createdAt)}</time>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
